import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import {
    GifPreview,
    LinkPreview,
    Reactions,
    ReactionsComponent,
    Threads,
    TimelineItem,
    TimelineItemType,
    Topic
} from "@teammood/timeline";
import { TranslateModule } from "@ngx-translate/core";
import { AsyncPipe, NgClass } from "@angular/common";
import { LinkyModule } from "@teammood/shared/util/linky";
import { EditTopicBoxComponent } from "../edit-topic-box/edit-topic-box.component";
import { GifPreviewComponent } from "../../../../shared/ui/gifpreview/gifpreview";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { distinctUntilChanged, map } from "rxjs";
import { MatTooltip } from "@angular/material/tooltip";
import { LinkPreviewComponent } from "../../../../shared/ui/linkpreview/linkpreview";
import { ThreadsComponent } from "../../../threads/threads.component";
import { MatMenuModule } from "@angular/material/menu";
import { UserInfos } from "../../../../shared/ui/username-icon/user-infos.type";
import { ReviewBadgeComponent } from "../../review-badge/review-badge.component";
import { ManagerPanelComponent } from "../../manager-panel/manager-panel.component";

@Component({
    selector: "tm-topic-card",
    templateUrl: "./topic-card.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        NgClass,
        LinkyModule,
        EditTopicBoxComponent,
        GifPreviewComponent,
        AsyncPipe,
        MatTooltip,
        LinkPreviewComponent,
        ReactionsComponent,
        ThreadsComponent,
        MatMenuModule,
        ReviewBadgeComponent,
        ManagerPanelComponent
    ]
})
export class TopicCardComponent implements OnInit {

    @Input({required: true})
    topic: Topic;

    @Input() isOdd: boolean;

    @Input() userInfos: UserInfos;

    protected showTimelineItemOptions: boolean;

    protected timelineItemEditMode: boolean;

    @Output()
    onEdit: EventEmitter<Topic> = new EventEmitter();

    @Output()
    onDelete: EventEmitter<Topic> = new EventEmitter();

    @Output()
    onModerate: EventEmitter<Topic> = new EventEmitter();

    @ViewChild('threadsComponent')
    private threadsComponent: ThreadsComponent;

    protected showThreads: boolean = true;

    protected optionEditText: string;
    protected optionDeleteText: string;
    protected optionReportText: string;

    protected isHandset$ =  this.breakpointObserver.observe([
        Breakpoints.Handset,
    ]).pipe(
        map(result => result.matches),
        distinctUntilChanged()
    )

    constructor(private breakpointObserver: BreakpointObserver) {
    }

    ngOnInit() {
        this.optionEditText = this.topic.type === 'topic' ? 'topic-card.options.edit'
                : this.topic.type === 'question' ? 'question-card.options.edit'
                    : this.topic.type === 'issue' ? 'issue-card.options.edit'
                        : undefined;

        this.optionDeleteText = this.topic.type === 'topic' ? 'topic-card.options.delete'
                : this.topic.type === 'question' ? 'question-card.options.delete'
                    : this.topic.type === 'issue' ? 'issue-card.options.delete'
                        : undefined;

        this.optionReportText = this.topic.type === 'topic' ? 'topic-card.options.report'
            : this.topic.type === 'question' ? 'question-card.options.report'
                : this.topic.type === 'issue' ? 'issue-card.options.report'
                    : undefined;

    }

    protected topicBeforeUpdate: Topic;

    protected editTopic() {
        if (this.topic.isEditable) {
            this.timelineItemEditMode = true;
            this.topicBeforeUpdate = structuredClone(this.topic);
        }
    }

    protected onTopicEdited(updatedTopic: Topic) {
        if (this.topic.isEditable) {
            this.onEdit.emit(updatedTopic);
        }
        this.timelineItemEditMode = false;
    }

    protected cancelEdit() {
        this.timelineItemEditMode = false;
        this.topic = structuredClone(this.topicBeforeUpdate);
    }

    protected deleteTopic() {
        if (this.topic.isDeletable) {
            this.onDelete.emit(this.topic);
        }
    }

    protected reportTopic() {
        if (!this.topic.isDeletable && !this.topic.isEditable) {
            this.onModerate.emit(this.topic);
        }
    }

    protected onThreadsUpdated(timelineItem: TimelineItem & Threads): void {
        if (timelineItem.itemType == "topicItem"){
            this.topic = timelineItem as Topic;
        }
    }

    protected onReacted(timelineItem: {id: string, itemType: TimelineItemType} & Reactions): void {
        if (timelineItem.itemType == "topicItem"){
            this.topic = timelineItem as Topic;
        }
    }

    protected toggleThreads() {
        this.showThreads = !this.showThreads;
    }

    protected discuss() {
        if (this.threadsComponent) {
            this.threadsComponent.discuss = true;
        }
    }

    toGifPreview(linkPreview: LinkPreview): GifPreview {
        return new GifPreview((linkPreview));
    }

    // fix a race condition by using a function
    getThreadsComponent: () => ThreadsComponent = () => this.threadsComponent;
}